import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import parse from "html-react-parser";

import {
  Container,
  Box,
  Grid,
  Typography,
  Button,
  Avatar,
  Paper,
  ButtonBase,
  Chip,
  ThemeProvider,
  useTheme
} from "@mui/material";

import LocationOnIcon from "@mui/icons-material/LocationOn";

import GoogleMap from "@aclymatepackages/google-maps";
import { hexToRgba } from "@aclymatepackages/converters";
import { mergeDarkTheme } from "@aclymatepackages/themes";
import { truncateText } from "@aclymatepackages/other-helpers";

import CnaughtPortfolioAllocationPieChart from "./CnaughtPortfolioAllocationPieChart";
import ProjectDetailsSlider from "./ProjectDetailsSlider";
import GrayPaper from "../../atoms/GrayPaper";
import Link from "../../atoms/mui/Link";
import Card from "../../atoms/mui/Card";

import usePortfoliosContext from "../../../helpers/contexts/offsetsPortfolios";

const HoverPaper = ({
  PaperComponent = Paper,
  onClick,
  buttonStyle = {},
  paperStyle = {},
  paperHoweverStyle = {},
  defaultElevation = 0,
  children,
  isSelected
}) => {
  const theme = useTheme();

  const [hover, setHover] = useState(false);

  return (
    <ButtonBase
      style={{ ...buttonStyle, borderRadius: theme.spacing(2) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      <PaperComponent
        style={{ ...paperStyle, ...(hover ? paperHoweverStyle : {}) }}
        elevation={isSelected || hover ? 3 : defaultElevation}
      >
        {children}
      </PaperComponent>
    </ButtonBase>
  );
};

const ImageGradientBox = ({ image, content }) => {
  const { palette } = useTheme();

  return (
    <Box
      style={{
        backgroundImage: `url(${image})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        position: "relative",
        height: "100%",
        width: "100%"
      }}
    >
      <Box
        position="absolute"
        style={{
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${hexToRgba(
            palette.primary.main,
            0.95
          )} 0%, ${hexToRgba(palette.secondary.main, 0.7)} 100%)`,
          color: "white"
        }}
        p={1}
      >
        <ThemeProvider theme={mergeDarkTheme}>{content}</ThemeProvider>
      </Box>
    </Box>
  );
};

const ProjectMarkerInfo = ({ marker: project }) => {
  const { primary_image_url: image, name } = project;

  const style = { width: "250px", height: "150px" };

  return (
    <HoverPaper buttonStyle={style} paperStyle={style}>
      <ImageGradientBox
        image={image}
        content={<Typography variant="h6">{name}</Typography>}
      />
    </HoverPaper>
  );
};

const PortfolioCategoryDisplayCard = ({
  category,
  allocated_fraction,
  selectedCategoryId,
  setSelectedCategoryId
}) => {
  const { name: categoryName, primary_image_url: image, id } = category;

  const [name, type] = categoryName.split("—");
  const isSelected = selectedCategoryId === id;

  return (
    <HoverPaper
      onClick={() => setSelectedCategoryId(id)}
      buttonStyle={{ height: "250px", width: "100%" }}
      paperStyle={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        opacity: isSelected ? 1 : 0.7
      }}
      isSelected={isSelected}
    >
      <ImageGradientBox
        image={image}
        content={
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <Typography variant="h6" color="textPrimary" align="left">
                {type}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                noWrap
                align="left"
              >
                {name}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="h3">
                {Math.round(100 * allocated_fraction)}%
              </Typography>
            </Grid>
          </Grid>
        }
      />
    </HoverPaper>
  );
};

const ProjectCard = ({ project }) => {
  const {
    name,
    summary,
    primary_image_url: image,
    location_name,
    id: projectId
  } = project;

  const { selectedPortfolio } = usePortfoliosContext();
  const { id: portfolioId } = selectedPortfolio || {};

  return (
    <Grid item xs={4}>
      <Link href={`/platform/company/purchase/${portfolioId}/${projectId}`}>
        <HoverPaper
          buttonStyle={{ width: "100%", height: "100%" }}
          PaperComponent={GrayPaper}
          paperStyle={{ height: "100%", padding: "16px" }}
        >
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Box
                style={{
                  height: "150px",
                  backgroundImage: `url(${image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
            </Grid>
            <Grid item>
              <Typography variant="h5" paragraph>
                {name}
              </Typography>
              <Typography variant="body2">{summary}</Typography>
            </Grid>
            <Grid item>
              <Chip
                color="secondary"
                style={{ color: "white" }}
                icon={<LocationOnIcon />}
                label={truncateText(location_name, 40)}
              />
            </Grid>
          </Grid>
        </HoverPaper>
      </Link>
    </Grid>
  );
};

const SelectedCategoryDisplayBlock = ({ projects = [], description }) => {
  const mapsFormattedProjects = projects.map(
    ({ location_latitude, location_longitude, ...otherProps }) => ({
      ...otherProps,
      latLng: { x: location_latitude, y: location_longitude },
      marker: {
        url: "/images/platform/icons/location-dot-solid.svg",
        scaledSize: new window.google.maps.Size(40, 40)
      }
    })
  );

  return (
    <>
      <Grid item>
        <Typography variant="body1">
          {description}{" "}
          <Link href="https://www.smithschool.ox.ac.uk/sites/default/files/2024-02/Oxford-Principles-for-Net-Zero-Aligned-Carbon-Offsetting-revised-2024.pdf">
            Learn more about Oxford Principles for Net Zero Aligned Carbon
            Offsetting
          </Link>
        </Typography>
      </Grid>
      <Grid item>
        <Card title="Projects In This Category" cardType="layout">
          <Box p={4}>
            <Grid container spacing={2} alignItems="stretch">
              {projects.map((project, idx) => (
                <ProjectCard
                  key={`category-project-card-${idx}`}
                  project={project}
                />
              ))}
            </Grid>
          </Box>
          <GoogleMap
            markers={mapsFormattedProjects}
            height="500px"
            InfoWindowComponent={ProjectMarkerInfo}
          />
        </Card>
      </Grid>
    </>
  );
};

const PortfolioDisplayBlock = ({ setPurchasePortfolio }) => {
  const { palette } = useTheme();
  const { action } = useParams();
  const history = useHistory();
  const { selectedPortfolio } = usePortfoliosContext();

  const {
    category_allocations: categories = [],
    description: portfolioDescription,
    displayPricePerTon
  } = selectedPortfolio || {};

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const selectedCategory = categories.find(
    (category) => category.category.id === selectedCategoryId
  );

  useEffect(() => {
    setSelectedCategoryId(null);
  }, [selectedPortfolio]);

  const selectedProject =
    action &&
    selectedCategory &&
    selectedCategory.category.projects.find((project) => project.id === action);

  return (
    <>
      {selectedProject && (
        <ProjectDetailsSlider
          project={selectedProject}
          onSlideClose={() => history.goBack()}
        />
      )}
      <Box p={4}>
        <Container>
          <Grid container direction="column" spacing={8}>
            <Grid item>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={5}>
                  <CnaughtPortfolioAllocationPieChart
                    {...selectedPortfolio}
                    selectedCategoryId={selectedCategoryId}
                    setSelectedCategoryId={setSelectedCategoryId}
                  />
                </Grid>
                <Grid item xs={7}>
                  <Grid container spacing={4} direction="column">
                    <Grid item>
                      <Typography variant="subtitle1">
                        {parse(portfolioDescription || "")}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      container
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            setPurchasePortfolio(selectedPortfolio)
                          }
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item>
                              <Box
                                p={1}
                                borderRadius={2}
                                style={{
                                  backgroundColor: "white"
                                }}
                              >
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    <Avatar
                                      color="primary"
                                      style={{
                                        color: "white",
                                        backgroundColor: palette.primary.main
                                      }}
                                    >
                                      <Typography variant="h3">$</Typography>
                                    </Avatar>
                                  </Grid>
                                  <Grid item>
                                    <Typography
                                      variant="h3"
                                      color="primary"
                                      display="inline"
                                    >
                                      {displayPricePerTon}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      style={{
                                        color: hexToRgba(
                                          palette.primary.main,
                                          0.8
                                        )
                                      }}
                                      display="inline"
                                    >
                                      /ton
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>
                            <Grid item>
                              <Typography style={{ fontSize: "1.5em" }}>
                                Purchase Credits
                              </Typography>
                            </Grid>
                          </Grid>
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container spacing={1}>
                {categories
                  .sort((a, b) => b.allocated_fraction - a.allocated_fraction)
                  .map((category, idx) => (
                    <Grid
                      item
                      xs={12 / categories.length}
                      key={`portfolio-category-${idx}`}
                    >
                      <PortfolioCategoryDisplayCard
                        {...category}
                        selectedCategoryId={selectedCategoryId}
                        setSelectedCategoryId={setSelectedCategoryId}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
            {selectedCategory && (
              <SelectedCategoryDisplayBlock {...selectedCategory.category} />
            )}
          </Grid>
        </Container>
      </Box>
    </>
  );
};
export default PortfolioDisplayBlock;
